import type { ProjectBillingPlanEmailTemplateInput } from "@src/__generated__/graphql";
import { formatMailContent } from "@src/utils/formatters";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { multipleEmails, required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable } from "mobx";

type Fields = {
  recipients: string;
  subject: string;
  content: string;
};

type EmailTemplate = {
  recipients: string[];
  subject?: string | null;
  content?: string | null;
};

export class SendEmailForm extends FormState<TToFieldStates<Fields>> {
  modalState = new DisclosureState({
    onOpen: () => {
      this.prefillFromTemplateIfEmpty();
    },
  });
  sendEmail;
  private template: EmailTemplate | undefined;

  constructor(
    formData: Fields & {
      send_email: boolean;
    },
  ) {
    super({
      recipients: new FieldState(formData.recipients).validators(
        required,
        multipleEmails,
      ),
      subject: new FieldState(formData.subject).validators(required),
      content: new FieldState(formatMailContent(formData.content)).validators(
        required,
      ),
    });
    makeObservable(this, {
      mutationInput: computed,
      isDirty: computed,
      formHasError: computed,
      setTemplate: action.bound,
      prefillFromTemplateIfEmpty: action.bound,
      resetToTemplate: action.bound,
      handleSwitchToggle: action.bound,
      handleModalClose: action.bound,
      handleModalConfirm: action.bound,
    });
    this.sendEmail = new FieldState(formData.send_email);
  }

  get formHasError(): boolean {
    return this.sendEmail.$ && this.hasError;
  }

  get mutationInput(): ProjectBillingPlanEmailTemplateInput {
    return {
      recipients: this.$.recipients.$.replaceAll(" ", "").split(","),
      subject: this.$.subject.$,
      content: this.$.content.$,
    };
  }

  get isDirty(): boolean {
    return (
      !!this.$.recipients.dirty ||
      !!this.$.subject.dirty ||
      !!this.$.content.dirty
    );
  }

  setTemplate(template: EmailTemplate | undefined) {
    this.template = template;
  }

  prefillFromTemplateIfEmpty() {
    if (!this.template) return;

    if (this.$.recipients.$ === "") {
      this.$.recipients.onChange(this.template.recipients.join(", "));
    }
    if (this.$.subject.$ === "") {
      this.$.subject.onChange(this.template.subject ?? "");
    }
    if (this.$.content.$ === "") {
      this.$.content.onChange(formatMailContent(this.template.content ?? ""));
    }
  }

  resetToTemplate() {
    if (!this.template) return;

    this.$.recipients = new FieldState(
      this.template.recipients.join(", "),
    ).validators(required, multipleEmails);
    this.$.subject = new FieldState(this.template.subject ?? "").validators(
      required,
    );
    this.$.content = new FieldState(
      formatMailContent(this.template.content ?? ""),
    ).validators(required);
  }

  handleSwitchToggle(isChecked: boolean) {
    this.sendEmail.onChange(isChecked);
    if (isChecked) {
      this.modalState.onOpen();
    } else {
      this.$.recipients.reset("");
      this.$.subject.reset("");
      this.$.content.reset("");
    }
  }

  handleModalClose() {
    this.modalState.close();
    this.reset();
    if (this.$.recipients.$ !== "") return;
    this.handleSwitchToggle(false);
  }

  async handleModalConfirm() {
    const { hasError } = await this.validate();
    if (hasError) return;
    const recipients = new String(this.$.recipients.$).valueOf();
    const subject = new String(this.$.subject.$).valueOf();
    const content = new String(this.$.content.$).valueOf();

    this.$.recipients = new FieldState(recipients).validators(
      required,
      multipleEmails,
    );
    this.$.subject = new FieldState(subject).validators(required);
    this.$.content = new FieldState(content).validators(required);

    this.modalState.close();
  }
}
